import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Save, PhoneCall, Radio } from "lucide-react";

const ACTIONS = [
  { value: "route_to_department", label: "Route to department" },
  { value: "play_message", label: "Play voice message" },
];

const DEFAULT_DEPARTMENTS = ["Sales", "Service", "Support", "Billing"];

export default function IVRBuilder() {
  const [menus, setMenus] = useState([]);
  const [active, setActive] = useState(null);
  const [departments, setDepartments] = useState(DEFAULT_DEPARTMENTS);

  const load = async () => {
    const { data } = await api.get("/ivr/menus");
    setMenus(data);
    if (data.length && !active) setActive(data[0]);
  };

  useEffect(() => {
    load();
    api
      .get("/departments")
      .then(({ data }) => setDepartments(data?.length ? data : DEFAULT_DEPARTMENTS))
      .catch(() => setDepartments(DEFAULT_DEPARTMENTS));
  }, []);

  const setNode = (i, patch) => {
    const nodes = [...active.nodes];
    nodes[i] = { ...nodes[i], ...patch };
    setActive({ ...active, nodes });
  };

  const addNode = () => {
    const nextKey = String(
      (active.nodes.reduce((m, n) => Math.max(m, parseInt(n.key || 0) || 0), 0) || 0) + 1
    );
    setActive({
      ...active,
      nodes: [
        ...active.nodes,
        { key: nextKey, label: "New option", action: "route_to_department", target: departments[0] || "Sales", message: "" },
      ],
    });
  };

  const removeNode = (i) => {
    const nodes = active.nodes.filter((_, j) => j !== i);
    setActive({ ...active, nodes });
  };

  const save = async () => {
    try {
      await api.put(`/ivr/menus/${active.id}`, {
        name: active.name,
        greeting: active.greeting,
        nodes: active.nodes,
      });
      toast.success("IVR menu saved");
      await load();
    } catch (e) {
      toast.error("Could not save menu");
    }
  };

  const createMenu = async () => {
    const { data } = await api.post("/ivr/menus", {
      name: "New Menu",
      greeting: "Welcome. Please choose an option.",
      nodes: [
        { key: "1", label: departments[0] || "Sales", action: "route_to_department", target: departments[0] || "Sales", message: "" },
      ],
    });
    await load();
    setActive(data);
    toast.success("Menu created");
  };

  if (!active) {
    return (
      <div className="p-10">
        <Button onClick={createMenu} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="ivr-create-empty">
          Create your first IVR menu
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="owner-ivr">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
            Voice
          </div>
          <h1 className="font-heading text-3xl font-bold mt-2">IVR Builder</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Design the greeting your customers hear and where each key press routes.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select
            value={active.id}
            onValueChange={(id) => setActive(menus.find((m) => m.id === id))}
          >
            <SelectTrigger className="w-[220px]" data-testid="ivr-menu-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {menus.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={createMenu} data-testid="ivr-new-menu">
            <Plus className="w-4 h-4 mr-1" /> New menu
          </Button>
          <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="ivr-save">
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2 border-slate-200 shadow-none">
          <CardHeader className="border-b">
            <CardTitle className="font-heading text-lg">Menu details</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={active.name}
                onChange={(e) => setActive({ ...active, name: e.target.value })}
                data-testid="ivr-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Greeting</Label>
              <Textarea
                rows={4}
                value={active.greeting}
                onChange={(e) => setActive({ ...active, greeting: e.target.value })}
                data-testid="ivr-greeting"
              />
              <p className="mono text-[11px] text-slate-500">
                Text-to-speech played when a customer calls in.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-3">
          <div className="relative border border-slate-200 rounded-lg bg-white p-6 grid-paper">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-md bg-[#0055FF] grid place-items-center text-white">
                <PhoneCall className="w-5 h-5" />
              </div>
              <div>
                <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                  Incoming call
                </div>
                <div className="font-heading font-semibold">{active.name}</div>
              </div>
            </div>

            <div className="space-y-3">
              {active.nodes.map((n, i) => (
                <div
                  key={i}
                  className="border border-slate-200 rounded-md p-4 bg-white hoverable-card"
                  data-testid={`ivr-node-${i}`}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="w-10 h-10 rounded bg-[#FF5000] text-white grid place-items-center font-heading font-bold mono">
                      {n.key}
                    </div>
                    <Input
                      value={n.label}
                      onChange={(e) => setNode(i, { label: e.target.value })}
                      placeholder="Label (e.g. Sales)"
                      className="max-w-[220px]"
                      data-testid={`ivr-node-label-${i}`}
                    />
                    <Select
                      value={n.action}
                      onValueChange={(v) => setNode(i, { action: v })}
                    >
                      <SelectTrigger className="w-[210px]" data-testid={`ivr-node-action-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACTIONS.map((a) => (
                          <SelectItem key={a.value} value={a.value}>
                            {a.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {n.action === "route_to_department" ? (
                      <Select
                        value={n.target || ""}
                        onValueChange={(v) => setNode(i, { target: v })}
                      >
                        <SelectTrigger className="w-[170px]" data-testid={`ivr-node-target-${i}`}>
                          <SelectValue placeholder="Choose team" />
                        </SelectTrigger>
                        <SelectContent>
                          {departments.map((d) => (
                            <SelectItem key={d} value={d}>
                              {d}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={n.message || ""}
                        onChange={(e) => setNode(i, { message: e.target.value })}
                        placeholder="Message to play"
                        className="flex-1 min-w-[200px]"
                        data-testid={`ivr-node-message-${i}`}
                      />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeNode(i)}
                      className="text-slate-400 hover:text-red-500"
                      data-testid={`ivr-node-remove-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={addNode}
                className="w-full border-dashed"
                data-testid="ivr-add-node"
              >
                <Plus className="w-4 h-4 mr-1" /> Add key press option
              </Button>
            </div>

            <div className="mt-6 border-t pt-4 mono text-[11px] text-slate-500 flex items-center gap-2">
              <Radio className="w-3.5 h-3.5" />
              Connect to Plivo / Exotel later — DialFlow exposes a
              <span className="text-slate-800"> /api/telephony/webhook/incoming </span>
              endpoint that your provider can hit.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
